/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import cx from 'classnames';
import styles from './styles.module.scss';
import azCommonStyles from '@/theme/globals.module.scss';
import Skeleton from '@/components/Skeleton/Skeleton';
import PdpPrice from '../PdpPrice';
import { countryCodes } from '@/constants/locale';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import { useFeatureFlag } from '@/features/globalConfig';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import type { SkuPricingAndAvailabilityFromAPI } from '@/types/availability';
import PriceInfoPopover from '../PriceInfoPopover';
import CorePriceInfoPopOver from '../CorePriceInfoPopover';
import CorePrice from '@/components/AZCustomComponent/CorePrice';
import Price from '@/components/AZCustomComponent/Price';
import { useAppData } from '@/hooks/useAppData';
import { getPopoverPrices } from './utils/getPopoverPrices';
import { getDealPrice } from './utils/getDealPrice';
import PriceV2 from '@/components/AZCustomComponent/Price/PriceV2';

const labelMap = {
  lblRegularPrice: 'label_regular_price',
  lblOrderSummaryCoreDeposit: 'label_cart_orderSummary_coreDeposit',
  lblRefundable: 'label_refundable',
  labelRefundableCoreCharge: 'label_refundable_core_charge',
  lbllineItemWhatIsIt: 'label_cart_lineItem_WhatIsIt',
  lblCoreDetails: 'pagecontent_cart_corePopup_CoreDetails',
  labelRegularPricePlusCore: 'label_regular_price_plus_core',
  lblSeeSavingsInCart: 'label_see_savings_in_cart',
  lblAddToCartDiscount: 'label_ShelfPage_AddToCartDiscount',
};

type PriceQuantityProps = {
  addedToCart: boolean;
  configurableSku: boolean;
  dealAvailable: boolean;
  discountedPrice: string | null;
  retailPrice: string[];
  availProduct?: SkuPricingAndAvailabilityFromAPI;
  staticPrice: number;
  count: number;
  saveNowOrRebateBadges: React.ReactNode | null;
  noGridSpacing?: boolean;
  unsetPriceMargin?: boolean;
};

const PdpPriceQuantity = (props: PriceQuantityProps) => {
  const {
    addedToCart,
    configurableSku,
    dealAvailable,
    discountedPrice,
    retailPrice,
    availProduct,
    count,
    staticPrice,
    saveNowOrRebateBadges,
    noGridSpacing,
    unsetPriceMargin,
  } = props;
  const isDealBadgeEnabled = useForteDecisionFlagWithTrueDefault('dealBadgeEnabled');
  const strikeThroughPricingEnabled = useFeatureFlag('SHOW_STRIKE_THROUGH_PRICING') === 'true';
  const { appData } = useAppData();
  const { isFetchingLocation } = appData;
  const labels = useLabels(labelMap);
  const locale = useLocale();
  const { data: storeData } = useStoreDetailsData();
  const isBopusEnabled = storeData?.bopusEnabled ?? false;
  const isBopusMexicoStoreEnabled = locale === countryCodes.mx && isBopusEnabled;
  const totalCorePrice = (availProduct?.corePrice ?? 0) * count;

  const [retailPriceDollars, retailPriceCents] = retailPrice;

  let retailPriceInt = parseFloat(`${retailPriceDollars}.${retailPriceCents}`);

  const coreBreakdownEnabled = useFeatureFlag('CORE_BREAKDOWN') === 'true';

  const dealPrice = getDealPrice(
    availProduct?.lowestPrice,
    strikeThroughPricingEnabled,
    discountedPrice,
    configurableSku,
    retailPrice,
  );

  const priceLabel = addedToCart ? labels.lblSeeSavingsInCart : labels.lblAddToCartDiscount;

  const strikethroughPrice = availProduct?.wasRetailPrice
    ? availProduct.wasRetailPrice
    : discountedPrice;

  const {
    popoverDiscountedPrice,
    popoverUpdatedPrice,
    popoverRetailPrice,
    popoverRetailPriceInt,
    popoverDealPrice,
  } = getPopoverPrices({
    wasRetailPrice:
      availProduct?.wasRetailPrice ??
      (availProduct?.configurableSku ? undefined : (availProduct?.retailPrice ?? undefined)),
    retailPriceInt,
    discountedPrice,
    count,
    staticPrice,
    dealPrice,
  });
  const totalPrice = (popoverDiscountedPrice + totalCorePrice).toString();
  const isProductDetailsPageV2Enabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');

  const isClearanceItem = Boolean(availProduct?.skuAvailabilityInfo?.clearanceItem);

  return (
    <div
      id="pdp-price"
      className={cx(styles.priceQuantityWrapper, {
        [styles.quantityMargin]: !configurableSku || availProduct?.corePrice,
        [styles.pdpRedesign]: isProductDetailsPageV2Enabled,
        [styles.makeColumn]: availProduct?.corePrice,
        [styles.noClearanceMargin]:
          isClearanceItem && isProductDetailsPageV2Enabled && availProduct?.corePrice,
      })}
      data-testid="price-quantity-wrapper"
    >
      <PdpPrice
        dealAvailable={dealAvailable}
        dealDrawerPDPEnabled={isDealBadgeEnabled}
        priceLabel={priceLabel}
        isClearanceItem={isClearanceItem}
        hasStrikethroughPrice={Boolean(strikethroughPrice)}
        discountedPrice={`${popoverDiscountedPrice}`}
        updatedDiscountedPrice={(popoverUpdatedPrice as number) ?? 0}
        retailPriceInt={popoverRetailPriceInt}
        corePriceInfoPopover={
          coreBreakdownEnabled ? (
            <CorePriceInfoPopOver
              totalCorePrice={totalCorePrice}
              isBopusMexicoStoreEnabled={isBopusMexicoStoreEnabled}
              totalPrice={totalPrice}
              dealPrice={popoverDealPrice}
              unsetMaxWidth={true}
            />
          ) : (
            <div className={cx(azCommonStyles['az-caption'])}>
              <div>
                {isFetchingLocation ? (
                  <Skeleton width={70} height={36} animation={'wave'} />
                ) : isProductDetailsPageV2Enabled ? (
                  <>
                    <PriceV2 priceType="primary" dealPrice={dealPrice} />
                  </>
                ) : (
                  <Price priceType="primary" dealPrice={dealPrice} />
                )}
              </div>
              <CorePrice corePrice={totalCorePrice} />
            </div>
          )
        }
        priceInfoPopover={
          <PriceInfoPopover
            discountedPrice={`${popoverDiscountedPrice}`}
            updatedDiscountedPrice={popoverUpdatedPrice ?? 0}
            configurableSku={configurableSku}
            retailPrice={popoverRetailPrice}
            retailPriceInt={popoverRetailPriceInt}
            itemQuantity={count}
          />
        }
        saveNowOrRebateBadges={saveNowOrRebateBadges}
        skuPricingAndAvailability={availProduct}
        count={count}
        noGridSpacing={noGridSpacing}
        unsetMargin={unsetPriceMargin}
      />
    </div>
  );
};

export default PdpPriceQuantity;
